/**
 * TableActionBar — the action button row that sits BELOW the PokerTable.
 *
 * Two display modes:
 *   - "idle" (default): Fold / Check / Call / Raise|Bet|Open buttons in a row.
 *   - "picking": preset sizing row (2.5×, 3×, etc.) + custom amount input.
 *
 * Lifted out of the old SpotCard.tsx during the poker-table redesign.
 * Behavior is identical to the old ActionArea + SizingRow + ActionButton —
 * pure relocate + restyle to sit below the table felt instead of inside
 * a structured card. The sizing math + preset rules are unchanged so the
 * isFacingBet regression test in SpotCard.test.ts still pins everything
 * load-bearing.
 */

import { useState } from "react";

import { formatBb } from "@/shared/format";
import { committedByPosition } from "@/features/chat/components/PokerTable";
import type { SpotAction, SpotPayload } from "@/shared/models";

export interface TableActionBarProps {
  spot: SpotPayload;
  locked: boolean;
  sizing: "idle" | "picking";
  facingBet: boolean;
  showCheck: boolean;
  callLabel: string;
  raiseLabel: string;
  raiseAction: SpotAction["action"];
  /** True when the raise action button represents a raise (preflop or
   *  facing-bet postflop). Currently unused inside the bar but kept on
   *  the props for parity with the old ActionArea signature — useful if
   *  a future visual treatment distinguishes raise-vs-bet styling. */
  isRaise: boolean;
  onPickSize: () => void;
  onCancelSize: () => void;
  onCommit: (action: SpotAction) => void;
}

export function TableActionBar(props: TableActionBarProps) {
  if (props.locked) {
    return (
      <div className="flex items-center justify-center gap-2 text-xs text-text-muted/80 pt-3">
        <span className="inline-block w-1 h-1 rounded-full bg-text-muted/60" />
        <span>Action submitted</span>
      </div>
    );
  }

  if (props.sizing === "picking") {
    return (
      <SizingRow
        label={sizingLabel(props)}
        presets={computePresets(props.spot, props.facingBet)}
        onPick={(amount) =>
          props.onCommit({ action: props.raiseAction, amount })
        }
        onCancel={props.onCancelSize}
      />
    );
  }

  return (
    <div className="flex flex-wrap justify-center gap-2.5 pt-3">
      <ActionButton
        label="Fold"
        variant="ghost"
        onClick={() => props.onCommit({ action: "fold" })}
      />
      {props.showCheck && (
        <ActionButton
          label="Check"
          variant="ghost"
          onClick={() => props.onCommit({ action: "check" })}
        />
      )}
      {(props.facingBet || props.spot.street === "preflop") && (
        <ActionButton
          label={props.callLabel}
          variant="primary"
          onClick={() => props.onCommit({ action: "call" })}
        />
      )}
      <ActionButton
        label={props.raiseLabel}
        variant="primary"
        onClick={props.onPickSize}
      />
    </div>
  );
}

/** Match the sizing-prompt label to the verb the action button used. */
function sizingLabel(props: TableActionBarProps): string {
  if (props.facingBet) return "Raise to";
  if (props.spot.street === "preflop") return "Open to";
  return "Bet";
}

function ActionButton({
  label,
  variant,
  onClick,
}: {
  label: string;
  variant: "primary" | "ghost";
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "px-5 py-2.5 rounded-xl text-sm font-medium transition-all min-h-[44px]",
        "active:scale-[0.98]",
        variant === "primary"
          ? "bg-accent text-white shadow-[0_1px_0_rgba(255,255,255,0.18)_inset,0_8px_20px_-8px_rgba(124,147,241,0.6)] hover:bg-accent/90"
          : "border border-border bg-transparent text-text hover:border-text-muted/40 hover:bg-white/[0.02]",
      ].join(" ")}
    >
      {label}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Sizing presets — context-aware: preflop-vs-postflop, facing-vs-leading.
// Lifted verbatim from the old SpotCard.tsx (no behavior change).
// ---------------------------------------------------------------------------

interface Preset {
  label: string;
  amount: number;
}

export function computePresets(spot: SpotPayload, facingBet: boolean): Preset[] {
  const isPreflop = spot.street === "preflop";
  const round1 = (v: number) => Math.round(v * 10) / 10;

  if (isPreflop) {
    const opener = largestNonHeroRaise(spot);
    if (opener) {
      return [
        { label: "2.5×", amount: round1(opener * 2.5) },
        { label: "3×", amount: round1(opener * 3) },
        { label: "4×", amount: round1(opener * 4) },
        { label: "All-in", amount: spot.stacks.hero },
      ];
    }
    return [
      { label: "2×", amount: 2.0 },
      { label: "2.5×", amount: 2.5 },
      { label: "3×", amount: 3.0 },
      { label: "4×", amount: 4.0 },
    ];
  }

  if (facingBet) {
    const prev = spot.history[spot.history.length - 1]?.amount ?? 0;
    if (prev > 0) {
      return [
        { label: "2.5×", amount: round1(prev * 2.5) },
        { label: "3×", amount: round1(prev * 3) },
        { label: "3.5×", amount: round1(prev * 3.5) },
        { label: "Pot+", amount: round1(spot.pot) },
      ];
    }
  }

  return [
    { label: "33%", amount: round1(spot.pot * 0.33) },
    { label: "50%", amount: round1(spot.pot * 0.5) },
    { label: "75%", amount: round1(spot.pot * 0.75) },
    { label: "Pot", amount: round1(spot.pot) },
  ];
}

function SizingRow({
  label,
  presets,
  onPick,
  onCancel,
}: {
  label: string;
  presets: Preset[];
  onPick: (amount: number) => void;
  onCancel: () => void;
}) {
  const [customOpen, setCustomOpen] = useState(false);
  const [draft, setDraft] = useState("");

  function commitCustom() {
    const amount = parseFloat(draft.trim());
    if (!Number.isFinite(amount) || amount <= 0) return;
    onPick(amount);
  }

  return (
    <div className="space-y-3 pt-3">
      <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-text-muted/80 text-center">
        {label}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
        {presets.map((p, i) => (
          <button
            key={i}
            type="button"
            onClick={() => onPick(p.amount)}
            className="rounded-xl border border-border bg-white/[0.02] hover:border-accent hover:bg-accent/10 transition py-2.5 px-2 flex flex-col items-center gap-0.5 active:scale-[0.98]"
          >
            <span className="text-sm font-semibold text-text">{p.label}</span>
            <span className="text-[11px] text-text-muted tabular-nums">
              {formatBb(p.amount)}
            </span>
          </button>
        ))}
      </div>

      {customOpen ? (
        <div className="flex items-stretch gap-2">
          <div className="flex-1 relative">
            <input
              autoFocus
              type="number"
              inputMode="decimal"
              step="0.5"
              min="0"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  commitCustom();
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  setCustomOpen(false);
                  setDraft("");
                }
              }}
              placeholder={`${label.toLowerCase()} (in big blinds)`}
              className="w-full bg-bg border border-border rounded-lg px-3 py-2 pr-9 text-text placeholder-text-muted/60 focus:outline-none focus:border-accent tabular-nums"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-text-muted pointer-events-none">
              bb
            </span>
          </div>
          <button
            type="button"
            onClick={commitCustom}
            disabled={
              !Number.isFinite(parseFloat(draft)) || parseFloat(draft) <= 0
            }
            className="px-4 rounded-lg bg-accent text-white text-sm font-medium hover:bg-accent/90 transition disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Commit
          </button>
          <button
            type="button"
            onClick={() => {
              setCustomOpen(false);
              setDraft("");
            }}
            aria-label="Close custom input"
            className="w-11 rounded-lg border border-border text-text-muted hover:text-text hover:border-text-muted/40 transition flex items-center justify-center"
          >
            ×
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setCustomOpen(true)}
            className="text-xs font-medium text-accent hover:text-accent/80 transition py-1.5 border border-accent/30 hover:border-accent/60 rounded-lg bg-accent/[0.05]"
          >
            Custom…
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="text-xs text-text-muted hover:text-text transition py-1.5 border border-border rounded-lg"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers — also used by SpotCard for the Call label.
// ---------------------------------------------------------------------------

/** Largest non-hero bet/raise across the whole history. Used to compute
 *  preflop preset sizing relative to the opener. */
function largestNonHeroRaise(spot: SpotPayload): number | null {
  let max = 0;
  for (const h of spot.history) {
    if (h.actor === "Hero") continue;
    if (
      (h.action === "bet" || h.action === "raise") &&
      h.amount &&
      h.amount > max
    ) {
      max = h.amount;
    }
  }
  return max > 0 ? max : null;
}

/** Most recent non-hero bet/raise amount — the to-LEVEL of the bet hero is
 *  facing. Used for sizing math. NOT the amount hero adds to call (that's
 *  `callAmount`, which subtracts what hero already has in). */
export function lastBetAmount(spot: SpotPayload): number {
  for (let i = spot.history.length - 1; i >= 0; i--) {
    const h = spot.history[i]!;
    if (
      h.amount &&
      (h.action === "bet" || h.action === "raise" || h.action === "call")
    ) {
      return h.amount;
    }
  }
  return 1;
}

/** The chips hero actually ADDS to call: the facing bet's to-level minus
 *  what hero already has committed on the current street, capped at hero's
 *  remaining stack. `allIn` is true when calling commits hero's whole stack.
 *
 *  Without subtracting hero's prior commitment, a 4-bet-pot "Call" overstates
 *  wildly (e.g. "Call 100bb" when hero put in 25 and has 75 left → really a
 *  75bb call all-in). Exported for SpotCard's "Call Xbb" label + tests. */
export function callAmount(spot: SpotPayload): { amount: number; allIn: boolean } {
  const heroPos = spot.position;
  // Hero's max commitment on the CURRENT street (already in front of hero).
  let heroStreetCommit = 0;
  for (const h of spot.history) {
    const actor = h.actor === "Hero" ? heroPos : h.actor;
    if (actor !== heroPos) continue;
    const street = h.street ?? spot.street;
    if (street !== spot.street) continue;
    if (h.amount && h.amount > heroStreetCommit) heroStreetCommit = h.amount;
  }
  if (spot.street === "preflop") {
    if (heroPos === "BB") heroStreetCommit = Math.max(heroStreetCommit, 1);
    else if (heroPos === "SB") heroStreetCommit = Math.max(heroStreetCommit, 0.5);
  }
  const remaining = Math.max(0, spot.stacks.hero - committedByPosition(spot, heroPos));
  const rawToCall = Math.max(0, lastBetAmount(spot) - heroStreetCommit);
  const amount = Math.min(rawToCall, remaining);
  return { amount, allIn: remaining > 0 && amount >= remaining };
}
